import { Component, computed, inject, OnInit, signal } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";

import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { IntpProjectModel, IntpSortedProjectModel } from "../../../models";
import { IntpProjectsApiService } from "../../../services";
import { DraggableSortListComponent } from "../../draggable-sort-list/draggable-sort-list";
import { FormDropdownMultiComponent } from "../../form";

@Component({
  selector: "adaa-intp-sort-projects-modal",
  imports: [TranslatePipe, FormDropdownMultiComponent, DraggableSortListComponent],
  templateUrl: "./intp-sort-projects-modal.html",
  styleUrl: "./intp-sort-projects-modal.scss",
})
export class IntpSortProjectsModalComponent implements OnInit {
  private readonly _intpProjectsApiService = inject(IntpProjectsApiService);
  readonly modal = inject(NgbActiveModal);

  //inputs
  countryId = signal<number | undefined>(undefined);

  //props
  private _projectsCopy = signal<IntpProjectModel[]>([]);
  selectedProjects = signal<number[]>([]);
  sortedProjects = signal<number[]>([]);

  projects = computed(() =>
    AdaaHelper.setDropdownArray(this._projectsCopy(), "projectId", AdaaHelper.getFieldLanguage("name"))
  );

  sortableProjects = computed(() => {
    const projects = this._projectsCopy()
      .filter((e) => this.selectedProjects().includes(e.projectId))
      .sort((a, b) => (a.projectOrder || 0) - (b.projectOrder || 0));

    return AdaaHelper.setDropdownArray(projects, "projectId", AdaaHelper.getFieldLanguage("name"));
  });

  public ngOnInit(): void {
    const countryId = this.countryId();
    if (!countryId) return;
    this._getProjects(countryId);
  }

  public save() {
    if (this.selectedProjects().length !== 5) return;

    const projectsToSave: IntpSortedProjectModel[] = [];

    this.sortedProjects().forEach((project, i) => {
      const orginalProject = this._projectsCopy().find((e) => e.projectId === project);

      if (orginalProject)
        projectsToSave.push({
          id: orginalProject.id,
          countryId: orginalProject.countryId,
          status: Constants.OBJECT_STATUS.ACTIVE,
          projectId: project,
          projectOrder: i + 1,
        });
    });

    const oldOrder = this._projectsCopy()
      .filter((e) => e.projectOrder != null)
      .sort((a, b) => (a.projectOrder || 0) - (b.projectOrder || 0))
      .slice(0, 5);

    oldOrder.forEach((project) => {
      const isExists = this.sortedProjects().some((e) => e === project.projectId);
      if (!isExists)
        projectsToSave.push({
          id: project.id,
          countryId: project.countryId,
          status: Constants.OBJECT_STATUS.ACTIVE,
          projectId: project.projectId,
          projectOrder: null,
        });
    });

    this._intpProjectsApiService.sortProjects(projectsToSave).subscribe({
      next: (response) => {
        if (response.inError) return;

        this.modal.close();
      },
    });
  }

  private _getProjects(countryId: number) {
    this._intpProjectsApiService.getProjectsByCountry(countryId).subscribe({
      next: (response) => {
        const data = response.responseData ?? [];

        data.forEach((e) => {
          e.nameAE = `${e.projectNameAE} (${e.sectorNameAE})`;
          e.nameEN = `${e.projectNameEN} (${e.sectorNameEN})`;
        });

        this._projectsCopy.set(response.responseData);

        // Get projects with projectOrder not null, sort by projectOrder, and get top 5
        const orderProjects = data
          .filter((e) => e.projectOrder != null)
          .sort((a, b) => (a.projectOrder || 0) - (b.projectOrder || 0))
          .slice(0, 5)
          .map((e) => e.projectId);

        this.selectedProjects.set(orderProjects);
        this.sortedProjects.set(orderProjects);
      },
    });
  }
}
